import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { validateUuid } from '@staff/shared'
import { useMetadata } from '@/composables/useMetadata'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { getStoredToken } from '@/services/auth'
import {
  createApplication,
  fetchDraftApplications,
  type ApplicationListItem,
  type ApplicationType,
} from '@/services/application'
import { createCommitmentApplication } from '@/services/commitment'
import { applicationRouteLocation } from '@/router/applicationRouteLocations'
import { isCommitmentApplication } from '@/utils/applicationDisplay'
import { createApplicationTypeValues } from '../applicationFormConfig'
import { createInitialFormState, useApplicationFormData } from './applicationFormData'

/**
 * Drives /ny-ansokan: pick a type and start a new application, or continue a saved draft.
 * Creating is the only place that knows about more than one application type - it posts
 * the draft to whichever service owns the type, then opens that type's own edit route.
 */
export const useCreateApplicationDraft = () => {
  const router = useRouter()
  const route = useRoute()
  const { getReferenceValues } = useMetadata()
  const { loadPersonnelDirectory, getPersonnelIdByUserId } = usePersonnelDirectory()
  const { form } = useApplicationFormData()

  const isCreatingDraft = ref(false)
  const isLoadingDrafts = ref(false)
  const error = ref('')
  const draftError = ref('')
  const draftApplications = ref<ApplicationListItem[]>([])
  const selectedApplicationType = ref<ApplicationType>('newLoanApplication')

  const createApplicationTypeOptions = computed(() =>
    getReferenceValues('applicationTypes')
      .filter((option) => (createApplicationTypeValues as readonly string[]).includes(option.code))
      .map((option) => ({ text: option.label, value: option.code })),
  )
  const getDraftApplicant = (application: ApplicationListItem) =>
    application.applicants?.find((applicant) => applicant.isMainApplicant)?.name || '-'

  const loadDraftApplications = async () => {
    isLoadingDrafts.value = true
    draftError.value = ''

    try {
      const applications = await fetchDraftApplications()
      draftApplications.value = applications
        .filter((application) => application.status === 'draft')
        .sort((a, b) => {
          const aUpdatedAt = a.updatedAt
          const bUpdatedAt = b.updatedAt
          const aTime = aUpdatedAt ? new Date(aUpdatedAt).getTime() : 0
          const bTime = bUpdatedAt ? new Date(bUpdatedAt).getTime() : 0
          return bTime - aTime
        })
    } catch (err) {
      draftError.value = err instanceof Error ? err.message : 'Kunde inte hämta utkast.'
    } finally {
      isLoadingDrafts.value = false
    }
  }

  const openDraft = async (application: ApplicationListItem) => {
    if (!application.id) return
    await router.push(applicationRouteLocation({ id: application.id, type: application.type, status: 'draft' }))
  }

  const createDraftAndOpen = async () => {
    if (isCreatingDraft.value) return

    const type = selectedApplicationType.value
    error.value = ''
    if (type === 'topupLoanApplication' && !form.debtId.trim()) {
      error.value = 'Debt ID krävs för höjning av befintliga lån.'
      return
    }
    if (type === 'topupLoanApplication' && !validateUuid(form.debtId.trim())) {
      error.value = 'Debt ID måste vara ett UUID.'
      return
    }

    isCreatingDraft.value = true

    try {
      await loadPersonnelDirectory()
      const brokerExternalId = getPersonnelIdByUserId(getStoredToken()?.payload?.id)
      if (!brokerExternalId) throw new Error('Kunde inte hitta inloggad användare i personallistan.')

      form.type = type
      const created = isCommitmentApplication(type)
        ? await createCommitmentApplication({ type, brokerExternalId })
        : await createApplication({
            type,
            brokerExternalId,
            ...(type === 'topupLoanApplication' ? { debtId: form.debtId.trim() } : {}),
          })

      if (!created.id) throw new Error('Saknar ansöknings-ID i create-svar.')
      await router.push(applicationRouteLocation({ id: created.id, type, status: 'draft' }))
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Kunde inte skapa ansökan.'
    } finally {
      isCreatingDraft.value = false
    }
  }

  onMounted(() => {
    // The form is a module-global singleton shared with the edit views - clear whatever
    // the last one left behind before offering a fresh start.
    Object.assign(form, createInitialFormState())
    selectedApplicationType.value = 'newLoanApplication'

    // Prefill from links like ?typ=topupLoanApplication&debtId=<uuid> (e.g. from the debt page).
    const { typ, debtId } = route.query
    if (typeof typ === 'string' && (createApplicationTypeValues as readonly string[]).includes(typ)) {
      selectedApplicationType.value = typ as ApplicationType
    }
    if (typeof debtId === 'string') form.debtId = debtId

    void loadDraftApplications()
    void loadPersonnelDirectory()
  })

  return {
    isCreatingDraft,
    isLoadingDrafts,
    error,
    draftError,
    draftApplications,
    selectedApplicationType,
    createApplicationTypeOptions,
    getDraftApplicant,
    loadDraftApplications,
    openDraft,
    createDraftAndOpen,
  }
}
